import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { SeverityDot } from "@/shared/ui/severity-dot";

import { useAssetLookup } from "../../api/useAssetLookup";
import { FindingView } from "../../findings/FindingView";
import { formatGameTime } from "../../format/formatGameTime";
import type { AnalysisPurchase } from "../../model/Analysis";
import type { Finding } from "../../model/Finding";
import { AssetIcon } from "../AssetIcon";

const TITLE = msg({ id: "analysis.went_well.title", message: "Went well" });
const ITEM_ON_TIME = msg({ id: "analysis.went_well.item", message: "{item} on time" });
const ITEM_TIMES = msg({
  id: "analysis.went_well.item_times",
  message: "At {bought}, usually {baseline}",
});

type WentWellProps = {
  // Сильные стороны из находок (`selectWentWellFindings`) — первыми.
  findings?: readonly Finding[];
  purchases: readonly AnalysisPurchase[];
};

const WENT_WELL_LIMIT = 3;

// «Получилось» рядом с «исправить в первую очередь»: сильные стороны из находок и ключевые
// предметы, купленные вовремя (`selectWentWell`), всего не больше трёх. Пусто — блока нет.
export const WentWell = ({ findings = [], purchases: allPurchases }: WentWellProps) => {
  const { _ } = useLingui();
  const findItem = useAssetLookup("items");
  const purchases = allPurchases.slice(0, Math.max(WENT_WELL_LIMIT - findings.length, 0));
  if (findings.length === 0 && purchases.length === 0) {
    return null;
  }
  return (
    <section
      aria-labelledby="analysis-went-well"
      className="flex flex-col gap-3 rounded-xl border border-good/40 bg-good/10 px-4.5 py-4"
    >
      <h2
        id="analysis-went-well"
        className="flex items-center gap-2 text-base font-semibold text-good"
      >
        <SeverityDot severity="good" />
        {_(TITLE)}
      </h2>
      <ul className="flex flex-col gap-3">
        {findings.map((finding, index) => (
          // Находки разбора не меняются после `done` — индекс как ключ безопасен.
          <li key={`finding-${index}`}>
            <FindingView finding={finding} variant="compact" />
          </li>
        ))}
        {purchases.map((purchase) => {
          const item = findItem(purchase.item_id);
          return (
            <li key={purchase.item_id} className="flex items-center gap-3">
              <AssetIcon asset={item} size={44} />
              <div className="flex min-w-0 flex-col">
                <h3 className="text-[15px] font-semibold">
                  {_({ ...ITEM_ON_TIME, values: { item: item.name } })}
                </h3>
                <p className="text-[13px] text-muted">
                  {_({
                    ...ITEM_TIMES,
                    values: {
                      bought: formatGameTime(purchase.bought_at_s),
                      baseline: formatGameTime(purchase.baseline_at_s ?? 0),
                    },
                  })}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
